import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { ArrowLeft, Save, Building2, MapPin, Phone, Mail, CreditCard, Receipt, Scale, FileText } from 'lucide-react';
import Select from 'react-select';
import { customSelectStylesLarge } from '../../../config/selectStyles';
import froalaConfig from '../../../config/froalaConfig.js';
import { showMessage } from '../../../scripts/showMessage.js';
import CardBasic from '../../ui/Card.jsx';
import { useCityLookup } from '../../../hooks/useCityLookup';
import { CityOptions } from '../../ui/Forms/CityOptions';

const FroalaEditor = React.lazy(() => import('react-froala-wysiwyg'));

// Styles defined OUTSIDE the component to prevent re-creation on every render
const cardStyle = {
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(44, 62, 80, 0.04)',
    marginBottom: '1.5rem',
    background: 'var(--bg-card, #ffffff)'
};

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid var(--border-light, #e2e8f0)',
    color: 'var(--text-primary, #2c3e50)',
    fontWeight: '600',
    fontSize: '1rem'
};

const labelStyle = {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)'
};

const inputStyle = {
    width: '100%',
    padding: '0.625rem 0.875rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    color: 'var(--text-primary, #2c3e50)',
    background: 'var(--bg-card, #ffffff)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    outline: 'none'
};

const actionBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    background: 'var(--bg-secondary, #f5f7f9)',
    borderBottom: '1px solid var(--border-light, #e2e8f0)'
};

const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid var(--border-medium, #cbd5e1)',
    borderRadius: '8px',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #2c3e50)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const saveButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1.25rem',
    border: 'none',
    borderRadius: '8px',
    background: 'var(--accent-slate, #5b7c8d)',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

// Static dropdown options defined OUTSIDE
const genderOptions = [
    { value: 0, label: '- - -' },
    { value: 1, label: 'Herr' },
    { value: 2, label: 'Frau' }
];

const countryOptions = [
    { value: 1, label: 'Deutschland' },
    { value: 2, label: 'Österreich' },
    { value: 3, label: 'Schweiz' },
    { value: 4, label: 'Spanien' },
    { value: 5, label: 'Italien' }
];

const taxObligationOptions = [
    { value: 1, label: 'Ja' },
    { value: 2, label: 'Nein' }
];

const BasedataSettings = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [titles, setTitles] = useState([]);

    const [formData, setFormData] = useState({
        title: null,
        gender: null,
        surname: '',
        name: '',
        company: '',
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        country: null,
        phone: '',
        fax: '',
        mobil: '',
        email: '',
        emailWedding: '',
        protocol: 'https',
        web: '',
        taxNumber: '',
        vatNumber: '',
        taxObligation: null,
        bank: '',
        iban: '',
        bic: '',
        accountHolder: '',
        privacy: '',
        agb: '',
        notes: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch('/index.php/settings/getBasedata');
                if (!response.ok) throw new Error('Failed to fetch basedata');
                const data = await response.json();
                const basedata = data.basedata || {};

                let protocol = 'https';
                let webUrl = basedata.web || '';
                if (webUrl.startsWith('http://')) {
                    protocol = 'http';
                    webUrl = webUrl.replace('http://', '');
                } else if (webUrl.startsWith('https://')) {
                    webUrl = webUrl.replace('https://', '');
                }

                const titleOptions = [
                    { value: 0, label: '- - -' },
                    ...(data.titles || []).map(t => ({ value: t.value, label: t.label }))
                ];

                setTitles(titleOptions);
                setFormData({
                    ...basedata,
                    title: titleOptions.find(t => t.value === basedata.title) || titleOptions[0],
                    gender: genderOptions.find(g => g.value === basedata.gender) || genderOptions[0],
                    country: countryOptions.find(c => c.value === basedata.country) || countryOptions[0],
                    taxObligation: taxObligationOptions.find(t => t.value === basedata.taxObligation) || taxObligationOptions[0],
                    protocol,
                    web: webUrl,
                    notes: basedata.notes || ''
                });
            } catch (error) {
                console.error('Error fetching basedata:', error);
                showMessage('Fehler beim Laden der Stammdaten', 'error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const setCityFromLookup = useCallback((ort) => {
        setFormData(prev => {
            if (prev.city && prev.city.trim() !== '') return prev;
            return { ...prev, city: ort };
        });
    }, []);

    const { cityOptions, selectCityOption } = useCityLookup(formData.zipCode, formData.country?.value, formData.city, setCityFromLookup);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setIsSaving(true);

        try {
            const fullWebUrl = formData.web ? `${formData.protocol}://${formData.web}` : '';

            const submitData = new FormData();
            submitData.append('title', formData.title?.value || 0);
            submitData.append('anrede', formData.gender?.value || 0);
            submitData.append('surname', formData.surname || '');
            submitData.append('name', formData.name || '');
            submitData.append('companyname', formData.company || '');
            submitData.append('street', formData.street || '');
            submitData.append('housenumber', formData.houseNumber || '');
            submitData.append('postcode', formData.zipCode || '');
            submitData.append('city', formData.city || '');
            submitData.append('country', formData.country?.value || 1);
            submitData.append('phonenumber', formData.phone || '');
            submitData.append('fax', formData.fax || '');
            submitData.append('mobil', formData.mobil || '');
            submitData.append('email', formData.email || '');
            submitData.append('email2', formData.emailWedding || '');
            submitData.append('web', fullWebUrl);
            submitData.append('tax', formData.taxNumber || '');
            submitData.append('ustid', formData.vatNumber || '');
            submitData.append('mwst', formData.taxObligation?.value || 1);
            submitData.append('bank', formData.bank || '');
            submitData.append('iban', formData.iban || '');
            submitData.append('bic', formData.bic || '');
            submitData.append('accountHolder', formData.accountHolder || '');
            submitData.append('privacy', formData.privacy || '');
            submitData.append('agb', formData.agb || '');
            submitData.append('notes', formData.notes);

            const response = await fetch('/index.php/settings/basedata_update', {
                method: 'POST',
                body: submitData
            });

            const data = await response.json();

            if (data.success) {
                showMessage('Stammdaten erfolgreich aktualisiert', 'success');
            } else {
                showMessage('Fehler beim Speichern der Stammdaten', 'error');
            }
        } catch (error) {
            console.error('Error saving basedata:', error);
            showMessage('Fehler beim Speichern der Stammdaten', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Lädt...</span>
                </div>
            </div>
        );
    }

    return (
        <CardBasic noPadding>
                    {/* Action Bar Top */}
                    <div style={actionBarStyle}>
                        <a href="index.php/settings" style={backButtonStyle}>
                            <ArrowLeft size={18} />
                            Zurück
                        </a>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSaving}
                            style={{ ...saveButtonStyle, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'wait' : 'pointer' }}
                        >
                            <Save size={18} />
                            {isSaving ? 'Speichert...' : 'Speichern'}
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-lg-8">
                                {/* Firma & Person */}
                                <div className="card" style={cardStyle}>
                                    <div className="card-body">
                                        <div style={sectionHeaderStyle}>
                                            <Building2 size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                            Firma & Person
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label style={labelStyle}>Firma</label>
                                                <input
                                                    type="text"
                                                    value={formData.company || ''}
                                                    onChange={(e) => handleChange('company', e.target.value)}
                                                    style={inputStyle}
                                                    placeholder="Firmenname"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label style={labelStyle}>Titel</label>
                                                <Select
                                                    value={formData.title}
                                                    onChange={(opt) => handleChange('title', opt)}
                                                    options={titles}
                                                    styles={customSelectStylesLarge}
                                                    placeholder="Titel..."
                                                    isClearable={false}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label style={labelStyle}>Anrede</label>
                                                <Select
                                                    value={formData.gender}
                                                    onChange={(opt) => handleChange('gender', opt)}
                                                    options={genderOptions}
                                                    styles={customSelectStylesLarge}
                                                    placeholder="Anrede..."
                                                    isClearable={false}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label style={labelStyle}>Vorname</label>
                                                <input
                                                    type="text"
                                                    value={formData.surname || ''}
                                                    onChange={(e) => handleChange('surname', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label style={labelStyle}>Nachname</label>
                                                <input
                                                    type="text"
                                                    value={formData.name || ''}
                                                    onChange={(e) => handleChange('name', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Anschrift */}
                                <div className="card" style={cardStyle}>
                                    <div className="card-body">
                                        <div style={sectionHeaderStyle}>
                                            <MapPin size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                            Anschrift
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-8">
                                                <label style={labelStyle}>Straße</label>
                                                <input
                                                    type="text"
                                                    value={formData.street || ''}
                                                    onChange={(e) => handleChange('street', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label style={labelStyle}>Hausnummer</label>
                                                <input
                                                    type="text"
                                                    value={formData.houseNumber || ''}
                                                    onChange={(e) => handleChange('houseNumber', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label style={labelStyle}>PLZ</label>
                                                <input
                                                    type="text"
                                                    value={formData.zipCode || ''}
                                                    onChange={(e) => handleChange('zipCode', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-5">
                                                <label style={labelStyle}>Ort</label>
                                                <input
                                                    type="text"
                                                    value={formData.city || ''}
                                                    onChange={(e) => handleChange('city', e.target.value)}
                                                    style={inputStyle}
                                                />
                                                <CityOptions options={cityOptions} onSelect={selectCityOption} />
                                            </div>
                                            <div className="col-md-4">
                                                <label style={labelStyle}>Land</label>
                                                <Select
                                                    value={formData.country}
                                                    onChange={(opt) => handleChange('country', opt)}
                                                    options={countryOptions}
                                                    styles={customSelectStylesLarge}
                                                    placeholder="Land..."
                                                    isClearable={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Kontakt */}
                                <div className="card" style={cardStyle}>
                                    <div className="card-body">
                                        <div style={sectionHeaderStyle}>
                                            <Phone size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                            Kontakt
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label style={labelStyle}>Telefon</label>
                                                <input
                                                    type="tel"
                                                    value={formData.phone || ''}
                                                    onChange={(e) => handleChange('phone', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label style={labelStyle}>Fax</label>
                                                <input
                                                    type="tel"
                                                    value={formData.fax || ''}
                                                    onChange={(e) => handleChange('fax', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label style={labelStyle}>Mobil</label>
                                                <input
                                                    type="tel"
                                                    value={formData.mobil || ''}
                                                    onChange={(e) => handleChange('mobil', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* E-Mail & Website */}
                                <div className="card" style={cardStyle}>
                                    <div className="card-body">
                                        <div style={sectionHeaderStyle}>
                                            <Mail size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                            E-Mail & Website
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label style={labelStyle}>E-Mail</label>
                                                <input
                                                    type="email"
                                                    value={formData.email || ''}
                                                    onChange={(e) => handleChange('email', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label style={labelStyle}>E-Mail Hochzeit</label>
                                                <input
                                                    type="email"
                                                    value={formData.emailWedding || ''}
                                                    onChange={(e) => handleChange('emailWedding', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label style={labelStyle}>Website</label>
                                                <div className="d-flex gap-2">
                                                    <select
                                                        value={formData.protocol}
                                                        onChange={(e) => handleChange('protocol', e.target.value)}
                                                        style={{ ...inputStyle, width: '110px', flexShrink: 0 }}
                                                    >
                                                        <option value="https">https://</option>
                                                        <option value="http">http://</option>
                                                    </select>
                                                    <input
                                                        type="text"
                                                        value={formData.web || ''}
                                                        onChange={(e) => handleChange('web', e.target.value)}
                                                        style={inputStyle}
                                                        placeholder="www.example.com"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Notizen */}
                                <div className="card" style={cardStyle}>
                                    <div className="card-body">
                                        <div style={sectionHeaderStyle}>
                                            <FileText size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                            Notizen
                                        </div>
                                        <Suspense fallback={<div className="text-center py-3">Editor lädt...</div>}>
                                            <FroalaEditor
                                                tag="textarea"
                                                model={formData.notes}
                                                onModelChange={(model) => handleChange('notes', model)}
                                                config={froalaConfig}
                                            />
                                        </Suspense>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="col-lg-4">
                                <div style={{ position: 'sticky', top: '1rem' }}>
                                    {/* Bankverbindung */}
                                    <div className="card" style={cardStyle}>
                                        <div className="card-body">
                                            <div style={sectionHeaderStyle}>
                                                <CreditCard size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                                Bankverbindung
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>Bank</label>
                                                <input
                                                    type="text"
                                                    value={formData.bank || ''}
                                                    onChange={(e) => handleChange('bank', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>IBAN</label>
                                                <input
                                                    type="text"
                                                    value={formData.iban || ''}
                                                    onChange={(e) => handleChange('iban', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>BIC</label>
                                                <input
                                                    type="text"
                                                    value={formData.bic || ''}
                                                    onChange={(e) => handleChange('bic', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Kontoinhaber</label>
                                                <input
                                                    type="text"
                                                    value={formData.accountHolder || ''}
                                                    onChange={(e) => handleChange('accountHolder', e.target.value)}
                                                    style={inputStyle}
                                                    placeholder="Optional, falls abweichend vom Firmennamen"
                                                />
                                                <small style={{ color: 'var(--text-secondary, #6c757d)', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                                                    Wird im QR-Code als Empfängername verwendet. Leer lassen, um den Firmennamen zu nutzen.
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Steuerliche Angaben */}
                                    <div className="card" style={cardStyle}>
                                        <div className="card-body">
                                            <div style={sectionHeaderStyle}>
                                                <Receipt size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                                Steuerliche Angaben
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>Steuernummer</label>
                                                <input
                                                    type="text"
                                                    value={formData.taxNumber || ''}
                                                    onChange={(e) => handleChange('taxNumber', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>UStID</label>
                                                <input
                                                    type="text"
                                                    value={formData.vatNumber || ''}
                                                    onChange={(e) => handleChange('vatNumber', e.target.value)}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Umsatzsteuerpflicht</label>
                                                <Select
                                                    value={formData.taxObligation}
                                                    onChange={(opt) => handleChange('taxObligation', opt)}
                                                    options={taxObligationOptions}
                                                    styles={customSelectStylesLarge}
                                                    placeholder="Auswählen..."
                                                    isClearable={false}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Rechtliches */}
                                    <div className="card" style={cardStyle}>
                                        <div className="card-body">
                                            <div style={sectionHeaderStyle}>
                                                <Scale size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                                                Rechtliches
                                            </div>
                                            <div className="mb-3">
                                                <label style={labelStyle}>Link zur Datenschutzerklärung</label>
                                                <input
                                                    type="text"
                                                    value={formData.privacy || ''}
                                                    onChange={(e) => handleChange('privacy', e.target.value)}
                                                    style={inputStyle}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Link zu den AGB</label>
                                                <input
                                                    type="text"
                                                    value={formData.agb || ''}
                                                    onChange={(e) => handleChange('agb', e.target.value)}
                                                    style={inputStyle}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>

                    {/* Action Bar Bottom */}
                    <div style={{ ...actionBarStyle, borderBottom: 'none', borderTop: '1px solid var(--border-light, #e2e8f0)' }}>
                        <a href="index.php/settings" style={backButtonStyle}>
                            <ArrowLeft size={18} />
                            Zurück
                        </a>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSaving}
                            style={{ ...saveButtonStyle, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'wait' : 'pointer' }}
                        >
                            <Save size={18} />
                            {isSaving ? 'Speichert...' : 'Speichern'}
                        </button>
                    </div>
            </CardBasic>
    );
};

export default BasedataSettings;
